import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";

import { useDashboardContext } from "../../../../../context/dashoard/DashboardProvider";
import { useMediaQueries } from "../../../../../shared";
import { StatusWidgetDto } from "../../../shared/DeviceStatus/types";
import useChartElements from "../../../shared/hooks/useChartElements";
import { useStyles as dashboardStyles } from "../../../styles";
import { CONNECTIVITY_TABS } from "../../../types";
import useCCstatus from "../hooks/useCCstatus";
import { CCStatusSummaryType } from "../types";

interface CCStatusProps {
  memoiseCCData: boolean;
  setMemoiseCCData: Dispatch<SetStateAction<boolean>>;
  setShowBackButton?: Dispatch<SetStateAction<boolean>>;
  setBackButton?: Dispatch<SetStateAction<Element | null>>;
  activeTab?: CONNECTIVITY_TABS;
}

const CCStatus = ({
  memoiseCCData,
  setMemoiseCCData,
  setShowBackButton,
  setBackButton,
  activeTab
}: CCStatusProps) => {
  const { setDashboardWidgets } = useDashboardContext();
  const { isLoading: isCCStatusLoading } = useCCstatus({
    memoiseCCData,
    setMemoiseCCData
  });

  const { toSm } = useMediaQueries();

  const { t } = useTranslation();

  const { isDashboardLoading, ccStatus: companyCardStatus } =
    useDashboardStore();

  const [hovered, setHovered] = useState<boolean>(false);
  const [hoveredElement, setHoveredElement] = useState<number | null>(null);
  const [hoveredListItem, setHoveredListItem] = useState<string | null>(null);
  const [showSingleStatusInfo, setShowSingleStatusInfo] =
    useState<CCStatusSummaryType | null>(null);

  const { generateStatusList, generateBackButton, generateStatusContent } =
    useChartElements({
      t
    });

  const { chartListBox, chartContainer } = dashboardStyles({
    toSm,
    fixedContentHeight: true
  });

  const isStatusLoading = isCCStatusLoading && !memoiseCCData;
  const isLoading = isDashboardLoading || isStatusLoading;

  useEffect(() => {
    const isShowSingleStatusInfo = Boolean(showSingleStatusInfo);

    // set back button when single status info is shown in Connectivity card
    setShowBackButton?.(isShowSingleStatusInfo);

    isShowSingleStatusInfo &&
      setBackButton?.(generateBackButton(handleGoBack) as any);

    !hovered && setHoveredElement(null);
  }, [
    hovered,
    hoveredElement,
    showSingleStatusInfo,
    companyCardStatus,
    setShowBackButton,
    setBackButton,
    generateBackButton
  ]);

  useEffect(() => {
    setDashboardWidgets(prevWidgets => ({
      ...prevWidgets,
      showCCstatus: true
    }));
    // close single status info when loading
    isLoading && setShowSingleStatusInfo(null);
  }, [isLoading, setDashboardWidgets]);

  const generateList = (ccStatus: StatusWidgetDto) =>
    generateStatusList({
      statusData: ccStatus,
      hoveredElement,
      hovered,
      hoveredListItem,
      setShowSingleStatusInfo,
      setHoveredListItem
    });

  const showSingleChartItemInfo =
    companyCardStatus && showSingleStatusInfo && !isLoading;

  const showListItemsInfo = companyCardStatus && !isLoading;

  const handleGoBack = () => {
    setShowSingleStatusInfo(null);
    setHoveredListItem(null);
    setHoveredElement(null);
    setHovered(false);
  };

  const statusContent = generateStatusContent({
    isLoading: isCCStatusLoading,
    showSingleChartItemInfo,
    showListItemsInfo,
    chartContainer,
    statusData: companyCardStatus,
    showSingleStatusInfo,
    hasCardWrapper: false,
    setHovered,
    setHoveredElement,
    setShowSingleStatusInfo,
    chartListBox,
    generateList,
    activeTab
  });

  return <>{statusContent}</>;
};

export default CCStatus;
